/**
 * Utility functions for handling image URLs
 */

/**
 * Get the correct image URL for display
 * @param {string} imageUrl - The image URL from the backend
 * @param {string} apiUrl - The API base URL (optional)
 * @returns {string} - The correct image URL to use
 */
export const getImageUrl = (imageUrl, apiUrl = process.env.REACT_APP_API_URL) => {
  if (!imageUrl) {
    console.log('🔍 getImageUrl: No image URL provided');
    return null;
  }

  console.log('🔍 getImageUrl input:', { imageUrl, apiUrl });

  // If it's already a full URL (http/https), use it directly
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    // Check for region mismatch and fix if needed
    if (imageUrl.includes('us-east-1') && imageUrl.includes('s3.amazonaws.com')) {
      imageUrl = imageUrl.replace('us-east-1', 'ap-south-1');
      console.log('🔍 getImageUrl: Fixed region mismatch:', { original: imageUrl, fixed: imageUrl });
    }
    
    // Fix for AWS S3 URLs with plus (+) character which gets misinterpreted as space
    imageUrl = imageUrl.replace(/\+/g, '%2B');
    
    console.log('🔍 getImageUrl: Using full URL directly:', imageUrl);
    return imageUrl;
  }

  // If it's a relative path, prepend the API URL
  if (apiUrl) {
    const fullUrl = `${apiUrl}${imageUrl}`;
    console.log('🔍 getImageUrl: Constructed full URL:', fullUrl);
    return fullUrl;
  }

  // Fallback to the original URL
  console.log('🔍 getImageUrl: Using fallback URL:', imageUrl);
  return imageUrl;
};

/**
 * Get the correct image URL for transformations (before/after images)
 * @param {string} imageUrl - The image URL from the backend
 * @returns {string} - The correct image URL to use
 */
export const getTransformationImageUrl = (imageUrl) => {
  console.log('🔍 getTransformationImageUrl:', imageUrl);
  return getImageUrl(imageUrl);
};

/**
 * Get the correct image URL for recipes
 * @param {string} imageUrl - The image URL from the backend
 * @returns {string} - The correct image URL to use
 */
export const getRecipeImageUrl = (imageUrl) => {
  console.log('🔍 getRecipeImageUrl:', imageUrl);
  return getImageUrl(imageUrl);
};

/**
 * Get the correct image URL for blog posts
 * @param {string} imageUrl - The image URL from the backend
 * @returns {string} - The correct image URL to use
 */
export const getBlogImageUrl = (imageUrl) => {
  console.log('🔍 getBlogImageUrl:', imageUrl);
  return getImageUrl(imageUrl);
};

/**
 * Get the correct image URL for reviews
 * @param {string} imageUrl - The image URL from the backend
 * @returns {string} - The correct image URL to use
 */
export const getReviewImageUrl = (imageUrl) => {
  console.log('🔍 getReviewImageUrl:', imageUrl);
  return getImageUrl(imageUrl);
};

/**
 * Debug function to log image data
 * @param {Object} item - The item containing image data
 * @param {string} type - The type of item (recipe, transformation, etc.)
 */
export const debugImageData = (item, type = 'unknown') => {
  console.log(`🔍 Debug ${type} image data:`, {
    id: item.id,
    title: item.title || item.client_name,
    image: item.image,
    image_url: item.image_url,
    before_image: item.before_image,
    after_image: item.after_image,
    fullItem: item
  });
};